import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import {
  CalendarHeart, CheckCircle2, MapPin, UsersRound,
} from "lucide-react";
import Axios from "../../services/AxiosInterceptor";
import HeroWithServicesInfo from "./HerowithInstruction";

const itemsPerPage = 6;
const STORAGE_URL = "https://yogamatic.in/api/";

const fadeUp = {
  hidden: { opacity: 0, y: 22 },
  show: { opacity: 1, y: 0 },
};

const DemoServices = () => {
  const [services, setServices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const navigate = useNavigate();

  useEffect(() => {
    const fetchServices = async () => {
      try {
        const response = await Axios.get("/api/get_demo_data");
        const data = response.data;
        if (data.success) {
          setServices(data.data || []);
        } else {
          setError("Failed to fetch demos");
        }
      } catch (err) {
        setError("Something went wrong while loading demo classes.");
      } finally {
        setLoading(false);
      }
    };

    fetchServices();
  }, []);

  const visibleServices = useMemo(() => {
    const startIndex = (currentPage - 1) * itemsPerPage;
    return services.slice(startIndex, startIndex + itemsPerPage);
  }, [currentPage, services]);

  const groupedServices = useMemo(() => {
    return visibleServices.reduce((grouped, service) => {
      (service.locations || []).forEach((location) => {
        if (!grouped[location.name]) grouped[location.name] = [];
        grouped[location.name].push(service);
      });
      return grouped;
    }, {});
  }, [visibleServices]);

  const totalPages = Math.ceil(services.length / itemsPerPage);

  const ServiceCard = ({ service, locationName, index }) => (
    <motion.button
      initial={{ opacity: 0, y: 22 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, amount: 0.1 }}
      transition={{ delay: (index % 3) * 0.06 }}
      whileHover={{ scale: 1.03, y: -3 }}
      whileTap={{ scale: 0.97 }}
      onClick={() => navigate(`/demo_details/${service._id}/${locationName}`)}
      className="ym-card ym-card-pad"
      style={{
        padding: "18px 16px",
        textAlign: "left",
        cursor: "pointer",
        border: "1px solid var(--ym-line)",
        background: "rgba(255,255,255,0.82)",
        display: "flex",
        flexDirection: "column",
        gap: 10,
      }}
    >
      <div style={{ display: "flex", alignItems: "flex-start", gap: 12 }}>
        <div
          style={{
            width: 44,
            height: 44,
            borderRadius: 10,
            overflow: "hidden",
            flexShrink: 0,
            background: "rgba(245,182,66,0.12)",
          }}
        >
          <img
            src={`${STORAGE_URL}upload/${service.image}`}
            alt={service.title}
            style={{ width: "100%", height: "100%", objectFit: "cover" }}
          />
        </div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontSize: 15, fontWeight: 850, color: "var(--ym-ink)" }}>
            {service.title}
          </div>
          <div style={{ fontSize: 12.5, color: "var(--ym-muted)", fontWeight: 600, marginTop: 3 }}>
            <UsersRound size={12} style={{ marginRight: 4, verticalAlign: -1 }} />
            {service.classMode || "Yoga class"} / {service.sessionType || "Session"}
          </div>
          <div style={{ fontSize: 12, color: "var(--ym-muted)", marginTop: 2 }}>
            <MapPin size={12} style={{ marginRight: 4, verticalAlign: -1 }} />
            {locationName}
          </div>
        </div>
        <motion.div
          initial={{ scale: 0 }}
          whileInView={{ scale: 1 }}
          style={{ marginTop: 2, flexShrink: 0 }}
        >
          <CheckCircle2 size={18} style={{ color: "var(--ym-gold)" }} />
        </motion.div>
      </div>
      <div
        className="ym-pill"
        style={{
          alignSelf: "flex-start",
          fontSize: 10.5,
          padding: "2px 10px",
          background: "rgba(245,182,66,0.12)",
          color: "var(--ym-gold-dark)",
        }}
      >
        Free Demo
      </div>
    </motion.button>
  );

  return (
    <main className="ym-page">
      <HeroWithServicesInfo
        eyebrow="Free demo classes"
        title="Try YogaMatic before you commit."
        text="Book a complimentary session, meet the instructor, and understand the class rhythm before choosing a regular plan."
      />

      <section className="ym-section">
        <div className="ym-container">
          <motion.div
            initial="hidden"
            whileInView="show"
            viewport={{ once: true, amount: 0.3 }}
            variants={fadeUp}
            style={{ textAlign: "center", maxWidth: 760, margin: "0 auto 38px" }}
          >
            <span className="ym-eyebrow" style={{ marginInline: "auto" }}>
              <CalendarHeart size={14} />
              Demo sessions
            </span>
            <h2 className="ym-title-small">Select a free introductory class.</h2>
            <p className="ym-lead" style={{ marginInline: "auto" }}>
              Choose the most convenient studio location and reserve your trial spot.
            </p>
          </motion.div>

          {loading && <div className="ym-loader">Loading demo classes...</div>}
          {error && !loading && <div className="ym-empty">{error}</div>}

          {!loading && !error && Object.keys(groupedServices).length === 0 && (
            <div className="ym-empty">No demo classes are available right now.</div>
          )}

          {!loading && !error && Object.entries(groupedServices).map(([locationName, locationServices]) => (
            <motion.section
              key={locationName}
              initial="hidden"
              whileInView="show"
              viewport={{ once: true, amount: 0.15 }}
              variants={fadeUp}
              style={{ marginTop: 42 }}
            >
              <div className="ym-meta-row" style={{ justifyContent: "space-between", marginBottom: 18 }}>
                <h3 className="ym-title-small" style={{ fontSize: "clamp(1.3rem, 2.5vw, 2rem)" }}>
                  {locationName}
                </h3>
                <span className="ym-pill">{locationServices.length} demos</span>
              </div>
              <div className="ym-grid-3">
                {locationServices.map((service, index) => (
                  <ServiceCard key={`${service._id}-${locationName}`} service={service} locationName={locationName} index={index} />
                ))}
              </div>
            </motion.section>
          ))}

          {totalPages > 1 && (
            <motion.div
              initial="hidden"
              whileInView="show"
              viewport={{ once: true }}
              variants={fadeUp}
              className="ym-actions"
              style={{ justifyContent: "center", marginTop: 42 }}
            >
              {Array.from({ length: totalPages }, (_, index) => index + 1).map((page) => (
                <button
                  key={page}
                  className={`ym-button ${currentPage === page ? "ym-button-gold" : "ym-button-ghost"}`}
                  onClick={() => setCurrentPage(page)}
                >
                  {page}
                </button>
              ))}
            </motion.div>
          )}
        </div>
      </section>
    </main>
  );
};

export default DemoServices;
